<template>
  <div class="data-table">
    <v-data-table
      :headers="developersTableHeaders"
      :items="developers"
      :options.sync="paginationOptions"
      :server-items-length="totals"
      :show-arrows="true"
      :footer-props="{
        'items-per-page-options': [20, 50, 100],
        'next-icon': 'mdi-menu-right',
        'prev-icon': 'mdi-menu-left',
        'disable-pagination': isDataLoading,
      }"
      :loading="isDataLoading"
      multi-sort
      no-data-text="No data"
      no-results-text="No data for that filters"
    >
      <template #item.full_name="{ item }">
        <router-link
          :to="{ name: 'hr-developer-show', params: { id: item.id } }"
          class="data-table__link"
        >
          <v-avatar
            size="32px"
            color="blue-light"
            class="mr-3"
          >
            <img
              v-if="item.avatar"
              alt="Avatar"
              src="item.avatar"
            >
            <span v-else class="white--text">
              {{ nameInitials(item.full_name) }}
            </span>
            <div class="data-table__icons">
              <div v-if="isVerified(item)" >
                <v-icon class="data-table__ico-verified" size="12" color="green">
                  mdi-checkbox-marked-circle
                </v-icon>
              </div>
              <div v-if="isTechnicalVerified(item)">
                <v-icon class="data-table__ico-verified" size="12" color="blue">
                  mdi-checkbox-marked-circle
                </v-icon>
              </div>
            </div>
          </v-avatar>
          <span class="data-table__name">{{ item.full_name }}</span>
        </router-link>
      </template>

      <template #item.role="{ value }">
        <span>
          <DataTableTooltipedCell :elements="value" />
        </span>
      </template>

      <template #item.level="{ value }">
        <span>{{ value }}</span>
      </template>

      <template #item.top_skills="{ value }">
        <span>
          <DataTableTooltipedCell :elements="value" />
        </span>
      </template>

      <template #item.rate="{ value }">
        <span> {{ rateDisplay(value) }}</span>
      </template>

      <template #item.status="{ value }">
        {{ value }}
      </template>

      <template #item.availability="{ value }">
        <DeveloperAvailabilityStatus :availability="value" />
      </template>

      <template #item.created_date="{ value }">
        <span>{{ value | displayDate}}</span>
      </template>

      <template #item.action="{ item }">
        <v-btn icon color="blue">
          <v-icon v-if="item.hr_favorite"
            medium
            @click="removeFavoerite(item.id)"
          >
            mdi-star
          </v-icon>
          <v-icon v-else
            medium
            @click="addFavorite(item.id)"
          >
            mdi-star-outline
          </v-icon>
        </v-btn>

        <v-menu left min-width="100">
          <template v-slot:activator="{ on }">
            <v-btn icon color="secondary" v-on="on">
              <v-icon large>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>

          <v-list dense elevation-2>
            <v-list-item link :to="{ name: 'hr-developer-show', params: { id: item.id } }">
              <v-list-item-title>Edit</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="item.membership_token"
              @click="copyInvitationLink(item.membership_token)"
            >
              <v-list-item-title>Survey Link</v-list-item-title>
            </v-list-item>
            <v-list-item @click="deleteElement(item.id)">
              <v-list-item-title>Delete</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <input id="refLinkToCopy"
      class="data-table__reflink"
      readonly
      :value="invitationLink"
    />

     <v-overlay :value="isDataLoading" absolute>
      <v-progress-circular indeterminate size="64"/>
    </v-overlay>

    <ConfirmDialog ref="confirmDialog" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { developersTableHeaders } from '@/helpers/tableHeaders';
import { copyToClipboard } from '@/helpers/utils';
import DeveloperAvailabilityStatus from '@/components/Developers/DeveloperAvailabilityStatus.vue';
import DataTableTooltipedCell from '@/components/Developers/DataTableTooltipedCell.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';

const pageUrl = process.env.VUE_APP_FE_BASE_URL;

export default {
  components: {
    DeveloperAvailabilityStatus,
    DataTableTooltipedCell,
    ConfirmDialog,
  },

  data() {
    return {
      developersTableHeaders,
      selectedToken: '',
      loadingData: false,
    };
  },

  computed: {
    ...mapState('hrDeveloper', [
      'developers',
    ]),

    paginationOptions: {
      get() {
        return this.pagination;
      },
      set(val) {
        this.setPagination(val);
      },
    },

    invitationLink() {
      return this.selectedToken ? `${pageUrl}/join/${this.selectedToken}` : '';
    },

    ...mapState('hrDevelopers', [
      'developers',
      'totals',
      'pagination',
      'isDataLoading',
    ]),
  },

  watch: {
    paginationOptions: {
      handler() {
        this.fetchDevelopers();
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('hrDevelopers', [
      'setPagination',
      'fetchDevelopers',
      'deleteDeveloper',
    ]),

    deleteElement(id) {
      this.$refs.confirmDialog
        .open({
          message: 'Are you sure you want to delete?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
        .then((confirm) => {
          if (confirm) {
            this.deleteDeveloper(id);
          }
        });
    },

    addFavorite(id) {
      this.$store.dispatch('favorites/addFavorite', id)
        .then(() => {
          this.fetchDevelopers();
        });
    },

    removeFavoerite(id) {
      this.$store.dispatch('favorites/removeFavorite', id)
        .then(() => {
          this.fetchDevelopers();
        });
    },

    async copyInvitationLink(token) {
      this.selectedToken = token;

      await this.$nextTick();

      copyToClipboard(
        'refLinkToCopy',
        this.copySucces,
        this.copyError,
      );
    },

    copySucces() {
      this.$store.dispatch('showSuccess', ['Link was copied ']);
    },

    copyError() {
      this.$store.dispatch('showError', ['Oops, unable to copy']);
    },

    nameInitials(name) {
      return name.split(' ').map((n) => n[0]).join('').substr(0, 2);
    },

    rateDisplay(rate) {
      return rate ? `${rate} / h` : '';
    },

    isVerified(item) {
      return item.verification_status === 'Verified';
    },

    isTechnicalVerified(item) {
      return item.technical_verification_status === 'Verified';
    },
  },
};
</script>

<style lang='scss' scoped>
.data-table {
  margin-bottom: 24px;
  padding: 12px 16px;
  background: #FFFFFF;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 8px;
  position: relative;

  &__link {
    text-decoration: unset !important;
    display: flex;
    align-items: center;
    position: relative;
  }

  &__name {
    max-width: 364px;
    word-break: break-word;
    white-space: nowrap;
    font-weight: 600;
  }

  &__reflink {
    opacity: 0;
    position: absolute;
    cursor: default;
  }

  &__icons {
    display: flex;
    position: absolute;
    bottom: -4px;
    div {
      background-color: white;
      line-height: 12px;
      border-radius: 50%;
      &:nth-of-type(2) {
        margin-left: 10px;
      }
    }
  }

  .v-btn:not(.v-btn--round).v-size--default {
    height: 36px;
    min-width: 64px;
    padding: 0 24px;
  }

  .v-avatar {
    overflow: visible;
  }
}
</style>
